import { Component, OnInit, OnChanges, Input, SimpleChanges } from '@angular/core';
import { DatePipe } from '@angular/common';
import { ConfirmationService } from 'primeng/api';

import { GrowlService, UserService } from '@app/core';

import { Format, Validation, ApprovalStatus, DateService, DialogStyle450 } from '@app/shared';

import { SharedPtable } from './shared-ptable';
import { SharedPtableService } from './shared-ptable.service';
import { SharedPtableDataService } from './shared-ptable-data.service';

/**
 * @author Jin Chun
 * @desc PrimeNG table component CRUD template. Instruction to create new component using this template
 * - create your component & service using cli
 *    - ng g component <component name>
 *    - cd <component name>
 *    - ng g service <component name>
 *    - ng g class <component name>
 * - all ts file change
 *    - copy and paste the content of the shared-ptable component, service, and class
 *    - change "SharedPtable" to new component name with title cased
 *    - change "Shared Ptable" to new component name
 *    - change "shared-ptable" to new component name
 *    - change "shared ptable" to new component name
 * - component ts file change
 *    - change inputArray to appropriate variable name using the Rename Symbol
 *    - change field names and headers in ngOnInit()
 */

@Component({
  selector: 'app-shared-ptable',
  templateUrl: './shared-ptable.component.html',
  styleUrls: ['./shared-ptable.component.css'],
  providers: [ConfirmationService, SharedPtableService, SharedPtableDataService]
})

export class SharedPtableComponent implements OnInit {

  sharedPtables: SharedPtable[];

  // standard format and validation
  DialogStyle450 = DialogStyle450;
  Format: typeof Format = Format;
  Validation: typeof Validation = Validation;         // validation length and message
  ApprovalStatus: typeof ApprovalStatus = ApprovalStatus;

  // main datatable objects
  selectedRow: SharedPtable;                          // selected row from the datatable
  selectedRowIndex: number;                           // index of the selected row
  tableColumns: any[];                                // column headers and fields to display on the table

  // dialog objects
  sharedPtableToEdit: SharedPtable;                            // object used when add/edit selected row
  displayDialog: boolean;                             // when true, show the popup dialog to gather user input

  // calendar validation properties
  minCalendarStartOn = new Date();                    // default to today
  minCalendarEndOn = this.minCalendarStartOn;         // default min EndOn = StartOn


  constructor (private datepipe: DatePipe, private dateService: DateService, private confirmationService: ConfirmationService, private user: UserService, private growl: GrowlService, private mainService: SharedPtableService, private dataService: SharedPtableDataService) { }

  /**
   * @desc initialize table columns, breacrumb and then get a list of shared ptable from the backend
   */
  ngOnInit() {
    this.tableColumns = [
      { field: 'fieldName', header: 'Field Header' },
    ];

    this.getSharedPtableAndTypes();
  }

  /**
   * @desc retrieve shared ptable supporting data from the backend DB
   * @returns this.typeDropDown - retreived shared ptable types as dropdown menu from the backend DB
   */
  getSharedPtableAndTypes() {

    // this.dataService.getSharedPtableAndTypes().subscribe(resArray => {
    //   this.inputArray = plainToClass(SharedPtable, resArray[0].list);
    //   this.leaveTypeDropdownOptions = resArray[1];
    // });

  }

  /**
   * @desc show a pop up dialog to gather new shared ptable information
   * @returns this.sharedPtableToEdit - created and submittedById set to the logged in userId
   *          this.minCalendarStartOn - set to today's date
   *          this.minCalendarEndOn - set to today's date
   *          this.displayDialog - set to true, so popup dialog is shown to gather a new shared ptable info
   */
  onClickAddRow() {

    this.sharedPtableToEdit = new SharedPtable();

    // set other properties as necessary

    // set the min dates for p-calendar
    this.minCalendarStartOn = new Date();
    this.minCalendarEndOn = this.minCalendarStartOn;

    this.displayDialog = true;
  }

  /**
   * @desc edit an existing shared ptable
   * @param row - selected shared ptable to edit
   * @returns this.copiedRow - has copy of the selected shared ptable
   *          this.minCalendarStartOn - set to today's date
   *          this.minCalendarEndOn - set to today's date
   *          displayDialog - set it to true which in turn open up an edit dialog
   */
  onClickEditRow(row: SharedPtable, rowIndex: number) {

    this.sharedPtableToEdit = new SharedPtable();
    Object.assign(this.sharedPtableToEdit, row);

    let index;
    if (row.id) {
      index = this.sharedPtables.findIndex(txn => txn.id == row.id);
    } else {
      // find matching row name since row id is not available because it is a replaced row
      index = this.sharedPtables.findIndex(txn => txn.name == row.name);
    }
    this.selectedRowIndex = index;

    // reset the min dates
    this.minCalendarStartOn = new Date();
    this.minCalendarEndOn = this.minCalendarStartOn;

    this.displayDialog = true;
  }

  /**
   * @desc set minCalendarEndOn based on the selected row.
   * @param row - with date selected
   * @returns this.minCalendarEndOn - set to one day later than the selected startOn
   */
  onSelectStartOn(row: SharedPtable) {

    const startOnDate = new Date(row.calendarStartOn);
    const endOnDate = new Date(row.calendarEndOn);

    // set minLeaveEndOn to one day after startOn - change it as necessary
    this.minCalendarEndOn = this.dateService.dateWithOffset(row.calendarStartOn, 1);

    if (row.calendarEndOn && startOnDate.getTime() >= endOnDate.getTime()) {
      row.calendarEndOn = null;
    }
  }

  /**
   * @desc save new shared ptable to the this.inputArray array
   * @param row - either new or selected shared ptable for edit
   * @returns: this.inputArray - new shared ptable added to the this array
   */
  onClickSaveRow(row: SharedPtable) {

    if (this.selectedRowIndex >= 0) {
      this.sharedPtables[this.selectedRowIndex] = row;
      this.selectedRowIndex = -1;
    } else {
      this.sharedPtables.unshift(row);
    }
    this.displayDialog = false;
  }

  /**
   * @desc delete the selected row from the inputArray
   * @param row - selected row to delete
   * @returns: this.inputArray - removed input row from the array
   */
  onClickDeleteRow(row: SharedPtable) {

    this.confirmationService.confirm({
      message: 'Are you sure that you want to delete this SharedPtable?',
      header: 'Delete SharedPtable',
      icon: 'fa fa-question-circle',
      accept: () => {
        let index;
        if (row.id) {
          index = this.sharedPtables.findIndex(txn => txn.id == row.id);
        } else {
          // find matching row name since row id is not available because it is a replaced row
          index = this.sharedPtables.findIndex(txn => txn.name == row.name);
        }

        if (index >= 0) {
          this.sharedPtables.splice(index, 1);
        }
      },
      reject: () => {
      }
    });
  }
}
